import React from "react";
import { Link } from "react-router-dom";
import { STATS, COMPANY, AI_IDENTITY } from "../content";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import AIOverview from "../components/AIOverview";

const PRINCIPLES = [
  { n: "I", t: "Single Contract", d: "One team, one timeline, one accountable partner — never subcontractor chaos." },
  { n: "II", t: "In-House Factory", d: "Dedicated joinery & upholstery factory in Al Sajja — zero third-party markups." },
  { n: "III", t: "Full Trade Stack", d: "Design, civil, demolition, MEP, joinery, soft furnishings, flooring, lighting, automation." },
  { n: "IV", t: "UAE-Proven", d: "Three decades of DM, DCD and DTCM approvals across the Emirates." },
];

export default function About() {
  usePageMeta({
    title: "About Time Furniture | UAE Interior Contractor Since 1993",
    description:
      "Time Furniture designs and delivers complete interior fit-out solutions across the UAE — concept, design, civil, MEP, joinery and finishing. Turnkey since 1993.",
    canonical: "https://timefurniture.ae/about",
    ogImage: "/media/revision-oct6/time-furniture-upholstery-craftsman-clean-workshop.webp",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      url: "https://timefurniture.ae/about",
      mainEntity: { "@id": "https://timefurniture.ae/#organization" },
      inLanguage: "en-AE",
    },
  });

  return (
    <div data-testid="about-page" className="bg-[#FAF5EC]">
      <Breadcrumbs
        testid="about-breadcrumbs"
        items={[
          { label: "Home", to: "/" },
          { label: "About" },
        ]}
      />
      {/* Heading */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-16">
        <p className="eyebrow mb-6">About · Est. 1993 · Dubai · Abu Dhabi · Sharjah</p>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl text-[#1C110C]">
          Turnkey Interior <em className="text-[#6E1F2C] not-italic">Contractor</em> in the UAE.
        </h1>
        <p className="mt-10 max-w-3xl text-[#3E2A22] leading-[1.8] text-base md:text-lg">
          <strong className="font-medium text-[#1C110C]">
            Time Furniture Turnkey Projects Contracting LLC
          </strong>{" "}
          is a UAE-based interior fit-out contractor and custom furniture
          manufacturer with three decades of work delivered across Dubai,
          Sharjah and the wider Emirates.
        </p>
      </section>

      {/* AI-readable identity block — clean entity facts for AI overviews & Google */}
      <section
        data-testid="about-identity"
        className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-4">
              <p className="eyebrow mb-5">Company at a Glance</p>
              <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#1C110C]">
                Who we <em className="text-[#6E1F2C] not-italic">are</em>.
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-5 text-[#3E2A22] leading-[1.8] text-base md:text-[17px]">
              {AI_IDENTITY.paragraphs.map((p, i) => (
                <p
                  key={i}
                  data-testid={`about-identity-paragraph-${i}`}
                  className={i === 0 ? "text-[#1C110C]" : ""}
                >
                  {p}
                </p>
              ))}
              <p className="pt-3 text-sm text-[#7A6B5F]">
                Read more about our{" "}
                <Link
                  to="/services"
                  className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
                >
                  interior fit-out services
                </Link>{" "}
                or{" "}
                <Link
                  to="/portfolio"
                  className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
                >
                  view completed projects across the UAE
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* AI OVERVIEW — answer-style entity block */}
      <AIOverview testid="about-ai-overview" />

      {/* OUR STORY */}
      <section
        data-testid="about-our-story"
        className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-32"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-6">Our Story</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#1C110C]">
              Three decades of <em className="text-[#6E1F2C] not-italic">craftsmanship</em>.
            </h2>
            <div className="mt-10 w-16 h-px bg-[#B8904E]" />
          </div>

          <div className="lg:col-span-8 lg:col-start-6 lg:-mt-2">
            <div className="max-w-[640px] space-y-7 text-[#3E2A22] leading-[1.8] text-[17px] md:text-[18px]">
              <p className="font-serif-display text-2xl md:text-3xl leading-[1.45] text-[#1C110C]">
                Time Furniture was built on decades of hands-on experience,
                craftsmanship, and a deep understanding of interiors.
              </p>

              <p>
                Vijayan Sahadevan, born in 1957 in Kerala, India, began his journey at a
                young age when he moved to Qatar in his early twenties. Over nearly a
                decade, he worked within the Qatar Sheikh Palace, where he was exposed
                to the highest standards of design, detailing, and execution. During
                this time, he also had the rare opportunity to travel internationally,
                visiting over 20 countries and experiencing global architecture and
                interiors first-hand.
              </p>

              <p>
                In 1992, he moved to the UAE and settled in Sharjah. A year later, in
                1993, he established a small curtain business in Abu Shagara. What began
                as a modest operation steadily grew through trust, quality, and
                consistency.
              </p>

              <p>
                Over the years, the business expanded into carpets, furniture, and
                joinery works, gradually building a reputation for reliable
                craftsmanship. After more than 15 years of growth, the company evolved
                into interior fit-out works, adapting to the changing needs of the
                market.
              </p>

              <p>
                From a single-category business, Time Furniture transformed into a full
                turnkey interior contracting company — delivering complete solutions
                from design to execution across residential, commercial, and hospitality
                projects.
              </p>

              <p className="text-[#1C110C]">
                Today, the company continues to build on the same foundation it started
                with: craftsmanship, trust, and long-term relationships.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section
        data-testid="about-founder"
        className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative overflow-hidden">
                <img
                  src="/team/vijayan-shadevan.webp"
                  alt="Vijayan Sahadevan, Founder of Time Furniture"
                  data-testid="founder-portrait"
                  className="w-full h-[520px] md:h-[620px] object-cover object-center"
                  loading="lazy"
                />
              </div>
              <div className="mt-8">
                <p className="font-serif-display text-2xl md:text-3xl text-[#1C110C]">
                  Vijayan Sahadevan
                </p>
                <p className="mt-2 text-[11px] tracking-[0.28em] uppercase text-[#7A6B5F]">
                  Founder
                </p>
              </div>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2">
              <p className="eyebrow mb-6">The Founder</p>
              <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02] tracking-tight text-[#1C110C]">
                From a single workshop in Abu Shagara — to a full turnkey contractor.
              </h2>

              <div className="mt-12 pl-8 md:pl-12 border-l-2 border-[#B8904E]">
                <p className="font-serif-display italic text-2xl md:text-4xl leading-[1.3] text-[#1C110C]">
                  “We built this company on craftsmanship, trust, and long-term
                  relationships.”
                </p>
                <p className="mt-6 text-[11px] tracking-[0.28em] uppercase text-[#6E1F2C]">
                  — Vijayan Sahadevan · Founder
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHERE WE ARE TODAY */}
      <section
        data-testid="about-where-we-are-today"
        className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-40"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-6">Where We Are Today</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#1C110C]">
              Every trade.{" "}
              <em className="text-[#6E1F2C] not-italic">One roof.</em>
            </h2>
            <div className="mt-10 w-16 h-px bg-[#B8904E]" />
          </div>

          <div className="lg:col-span-8 lg:col-start-6">
            <div className="max-w-[640px] space-y-7 text-[#3E2A22] leading-[1.8] text-[17px] md:text-[18px]">
              <p>
                With over three decades of experience, Time Furniture has grown into a
                full-service interior fit-out and turnkey contractor in the UAE,
                handling projects across Dubai, Abu Dhabi, Sharjah and the wider Emirates.
              </p>

              <p>
                The company offers a complete range of services under one roof,
                including civil works, MEP services, custom joinery, in-house
                upholstery, furniture manufacturing, curtains, blinds, flooring, and
                full renovation solutions.
              </p>

              <p>
                What sets Time Furniture apart is its ability to manage every aspect of
                a project internally — from concept to completion — ensuring quality,
                consistency, and attention to detail at every stage.
              </p>

              <p className="text-[#1C110C]">
                Today, the company works across residential, commercial, and hospitality
                sectors, delivering tailored solutions that combine functionality,
                aesthetics, and long-term durability.
              </p>
            </div>

            <p className="mt-12 text-xs text-[#7A6B5F] tracking-[0.22em] uppercase">
              {COMPANY.tagline}
            </p>
          </div>
        </div>

        {/* Our teams on site — genuine photographs */}
        <div className="mt-16 md:mt-24" data-testid="about-team-on-site">
          <p className="eyebrow mb-6">Our Teams On Site</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {[
              { src: "/media/owner-projects/time-furniture-installation-team-joinery-time-furniture.webp", w: 1280, h: 588, alt: "Time Furniture installation team assembling joinery on site", cap: "Joinery installation" },
              { src: "/media/owner-projects/time-furniture-site-fit-out-team-time-furniture.webp", w: 2000, h: 1500, alt: "Time Furniture team working on a site fit-out", cap: "Site fit-out in progress" },
              { src: "/media/owner-projects/time-furniture-wardrobe-installation-team-time-furniture.webp", w: 1280, h: 588, alt: "Time Furniture team installing fitted wardrobe shelving", cap: "Wardrobe installation" },
            ].map((t, i) => (
              <figure key={t.src}>
                <img
                  src={t.src}
                  width={t.w}
                  height={t.h}
                  loading="lazy"
                  decoding="async"
                  alt={t.alt}
                  data-testid={`about-team-img-${i}`}
                  className="w-full aspect-[4/3] object-cover object-center bg-[#EDE1C8]"
                />
                <figcaption className="mt-2 text-[10px] tracking-[0.14em] uppercase text-[#3E2A22]">{t.cap}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div
              key={s.label}
              data-testid={`about-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <p className="font-serif-display text-5xl md:text-7xl text-[#6E1F2C]">
                {s.value}
              </p>
              <p className="mt-3 text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F]">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Principles */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-28 md:py-40 lg:py-48">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-5">How We Work</p>
            <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight text-[#1C110C]">
              Four principles.
            </h2>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#6E1F2C]/15">
            {PRINCIPLES.map((p) => (
              <div
                key={p.n}
                className="bg-[#FAF5EC] p-10"
                data-testid={`principle-${p.t.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <p className="font-serif-display text-4xl text-[#6E1F2C]">{p.n}</p>
                <p className="mt-4 font-serif-display text-2xl text-[#1C110C]">{p.t}</p>
                <p className="mt-3 text-sm text-[#7A6B5F] leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F2E9D8] border-t border-[#6E1F2C]/15">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 text-center">
          <p className="eyebrow mb-5">Next</p>
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight text-[#1C110C]">
            Explore the <em className="text-[#6E1F2C] not-italic">ten disciplines</em>{" "}
            we deliver under one roof.
          </h2>
          <Link
            to="/services"
            data-testid="about-cta-services"
            className="mt-10 inline-flex items-center gap-3 bg-[#6E1F2C] text-white px-10 py-5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors"
          >
            View All Fit-Out Services
          </Link>
        </div>
      </section>
    </div>
  );
}
